import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { WAITLIST_SOURCE_HERO } from '@/lib/constants';
import { useLang } from '@/i18n/LanguageContext';

type Pane = 'request' | 'invite';

interface CTAProps {
  pane: Pane;
  setPane: (p: Pane) => void;
}

export default function CTA({ pane, setPane }: CTAProps) {
  const navigate = useNavigate();
  const { lang, t } = useLang();

  // Request access (waitlist)
  const [email, setEmail] = useState('');
  const [reqState, setReqState] = useState<'idle' | 'loading' | 'ok' | 'err'>('idle');
  const [reqMsg, setReqMsg] = useState('');

  // Invite code
  const [code, setCode] = useState('');

  const submitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || reqState === 'loading') return;
    setReqState('loading');
    setReqMsg('');
    try {
      const res = await api.submitWaitlist(value, WAITLIST_SOURCE_HERO);
      setReqState('ok');
      setReqMsg(
        res.message ||
          (lang === 'zh'
            ? '✓ 已加入名单——轮到你时我们会发邮件通知。'
            : "✓ You're on the list — we'll email you when you're in.")
      );
    } catch (err) {
      setReqState('err');
      setReqMsg(
        (err as Error)?.message ||
          (lang === 'zh' ? '出了点问题——请重试。' : 'Something went wrong — please try again.')
      );
    }
  };

  const submitInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const value = code.trim().toUpperCase();
    if (!value) return;
    navigate(`/beta?code=${encodeURIComponent(value)}`);
  };

  return (
    <section className="section" id="cta" data-screen-label="Final CTA">
      <div className="container">
        <div className="cta reveal">
          <span className="eyebrow eyebrow--plain">{t('cta.eyebrow')}</span>
          <h2 style={{ marginTop: 18 }}>
            {t('cta.h2.line1')}
            <br />
            {t('cta.h2.line2')}
          </h2>
          <p>{t('cta.p')}</p>

          <div className="cta-switch">
            <button
              className={`cta-tab${pane === 'request' ? ' active' : ''}`}
              onClick={() => setPane('request')}
            >
              {t('cta.tab.request')}
            </button>
            <button
              className={`cta-tab${pane === 'invite' ? ' active' : ''}`}
              onClick={() => setPane('invite')}
            >
              {t('cta.tab.invite')}
            </button>
          </div>

          <div>
            {/* request access */}
            <div className={`cta-pane${pane === 'request' ? ' active' : ''}`}>
              <form className="signup" onSubmit={submitRequest}>
                <input
                  type="email"
                  placeholder={t('cta.email.placeholder') as string}
                  aria-label="Work email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (reqState !== 'idle') {
                      setReqState('idle');
                      setReqMsg('');
                    }
                  }}
                />
                <button className="btn" type="submit" disabled={reqState === 'loading'}>
                  {reqState === 'loading' ? t('cta.request.sending') : t('cta.request.btn')}
                </button>
              </form>
              {(reqState === 'ok' || reqState === 'err') && (
                <p className={`cta-help${reqState === 'ok' ? ' ok' : ' err'}`}>{reqMsg}</p>
              )}
            </div>
            {/* invite code */}
            <div className={`cta-pane${pane === 'invite' ? ' active' : ''}`}>
              <form className="signup invite" onSubmit={submitInvite}>
                <input
                  type="text"
                  placeholder={t('cta.invite.placeholder') as string}
                  aria-label="Invite code"
                  autoComplete="off"
                  spellCheck={false}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <button className="btn" type="submit">
                  {t('cta.invite.btn')}
                </button>
              </form>
              <p className="cta-help">
                {t('cta.invite.help.pre')}
                <a
                  href="#cta"
                  onClick={(e) => {
                    e.preventDefault();
                    setPane('request');
                  }}
                >
                  {t('cta.invite.help.link')}
                </a>
              </p>
            </div>
          </div>

          <div className="chips">
            <span className="chip">
              <span className="dot" />
              {t('cta.chip.free')}
            </span>
            <span className="chip">{t('cta.chip.cancel')}</span>
            <span className="chip">{t('cta.chip.gmail')}</span>
            <span className="chip">{t('cta.chip.migration')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
